/* Bundled from variant-selects/product-information/product-gallery/fade-card/buy-now-button JS — behavior-equivalent since classic scripts share one global scope; add-to-cart-button.js stays separate since sections/product.liquid also loads it independently. */

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.selectedOptions = {};
  }

  connectedCallback() {
    this.productData = this.getProductData();
    if (!this.productData) return;

    this.initializeOptions();
    this.bindColorSwatches();
    this.bindSizeOptions();
    this.updateColorSwatchAvailability();

    setTimeout(() => {
      this.initializeGallery();
      this.initializeButtonState();
    }, 100);
  }

  initializeGallery() {
    const activeColor = this.querySelector(".color-swatch.active");
    if (activeColor) {
      const colorValue = activeColor.dataset.colorValue;
      if (colorValue) {
        this.updateGalleryForColor(colorValue);
      }
    }
  }

  initializeButtonState() {
    const activeColor = this.querySelector(".color-swatch.active");
    if (!activeColor) return;

    const colorValue = activeColor.dataset.colorValue;
    if (!colorValue) return;

    const hasAvailableSize = this.hasAvailableSizeForColor(colorValue);

    if (!hasAvailableSize) {
      this.dispatchVariantChange(null, true);
    } else {
      const variant = this.findVariant();
      if (variant) {
        this.dispatchVariantChange(variant, false);
      } else {
        this.dispatchVariantChange(null, false);
      }
    }
  }

  getProductData() {
    const scriptTag = this.querySelector('script[type="application/json"]');
    if (!scriptTag) {
      console.error("VariantSelects: Product JSON not found");
      return null;
    }

    try {
      return JSON.parse(scriptTag.textContent);
    } catch (error) {
      console.error("VariantSelects: Failed to parse product JSON", error);
      return null;
    }
  }

  initializeOptions() {
    const activeColor = this.querySelector(".color-swatch.active");
    if (activeColor) {
      this.selectedOptions.Color = activeColor.dataset.colorValue;
    }

    const activeSize = this.closest(".product-information")?.querySelector(
      ".variant-size-option.active"
    );
    if (activeSize) {
      this.selectedOptions.Size = activeSize.dataset.optionValue;
    }
  }

  bindColorSwatches() {
    this.querySelectorAll(".color-swatch").forEach((swatch) => {
      swatch.addEventListener("click", (e) => {
        e.preventDefault();
        this.onColorChange(swatch);
      });
    });
  }

  bindSizeOptions() {
    const productInfo = this.closest(".product-information");
    if (!productInfo) return;

    productInfo.querySelectorAll(".variant-size-option").forEach((option) => {
      option.addEventListener("click", () => {
        if (!option.classList.contains("sold-out")) {
          this.onSizeChange(option);
        }
      });
    });
  }

  onColorChange(swatch) {
    const colorValue = swatch.dataset.colorValue;
    if (!colorValue) return;

    this.selectedOptions.Color = colorValue;

    this.querySelectorAll(".color-swatch").forEach((s) =>
      s.classList.remove("active")
    );
    swatch.classList.add("active");

    const label = this.querySelector(".color-selected-value");
    if (label) label.textContent = colorValue;

    this.updateGalleryForColor(colorValue);

    this.updateSizeAvailability();
    this.updateColorOutOfStockMessage();

    const previousSize = this.selectedOptions.Size;
    if (previousSize) {
      const sizeVariant = this.productData.variants.find(
        (v) => v.options[0] === colorValue && v.options[1] === previousSize
      );

      if (!sizeVariant || !sizeVariant.available) {
        this.clearSizeSelection();
      }
    }

    const variant = this.findVariant();

    const variantForPriceUpdate =
      variant || this.findFirstVariantForColor(colorValue);

    this.updatePageContent(variantForPriceUpdate);

    this.updateModelSize(variantForPriceUpdate);

    const hasAvailableSize = this.hasAvailableSizeForColor(colorValue);

    if (variant) {
      this.updateURL(variant.url);
      this.dispatchVariantChange(variant, false);
    } else {
      this.dispatchVariantChange(null, !hasAvailableSize);
    }
  }

  onSizeChange(option) {
    const size = option.dataset.optionValue;
    if (!size) return;

    this.selectedOptions.Size = size;

    const productInfo = this.closest(".product-information");
    productInfo
      .querySelectorAll(".variant-size-option")
      .forEach((o) => o.classList.remove("active"));
    option.classList.add("active");

    this.hideSizeNotSelectedMessage();

    const variant = this.findVariant();

    if (variant) {
      this.updateURL(variant.url);
      this.updateModelSize(variant);
    }

    this.updatePageContent(variant);

    if (typeof window.updateSizeChart === "function") {
      window.updateSizeChart(size);
    }

    this.dispatchVariantChange(variant);
  }

  findVariant() {
    const { Color, Size } = this.selectedOptions;

    const hasSizeOption = this.productData.variants.some((v) => v.options[1]);

    if (hasSizeOption && (!Color || !Size)) {
      return null;
    }

    return this.productData.variants.find((v) => {
      if (Color && Size) {
        return v.options[0] === Color && v.options[1] === Size;
      } else if (Color) {
        return v.options[0] === Color;
      } else if (Size) {
        return v.options[1] === Size;
      }
      return false;
    });
  }

  findFirstVariantForColor(colorValue) {
    return this.productData.variants.find((v) => v.options[0] === colorValue);
  }

  hasAvailableSizeForColor(colorValue) {
    return this.productData.variants.some(
      (v) => v.options[0] === colorValue && v.available === true
    );
  }

  updateColorSwatchAvailability() {
    this.querySelectorAll(".color-swatch").forEach((swatch) => {
      const colorValue = swatch.dataset.colorValue;
      if (!colorValue) return;

      const hasAvailable = this.hasAvailableSizeForColor(colorValue);
      swatch.classList.toggle("sold-out", !hasAvailable);
    });

    this.updateColorOutOfStockMessage();
  }

  updateColorOutOfStockMessage() {
    const productInfo = this.closest(".product-information");
    if (!productInfo) return;

    const message = productInfo.querySelector(".color-out-of-stock-message");
    if (!message) return;

    const activeSwatch = this.querySelector(".color-swatch.active");
    const isActiveSoldOut = activeSwatch && activeSwatch.classList.contains("sold-out");

    message.style.display = isActiveSoldOut ? "flex" : "none";
  }

  updateSizeAvailability() {
    const productInfo = this.closest(".product-information");
    if (!productInfo) return;

    const selectedColor = this.selectedOptions.Color;
    const sizeOptions = productInfo.querySelectorAll(".variant-size-option");

    sizeOptions.forEach((option) => {
      const size = option.dataset.optionValue;

      if (selectedColor) {
        const variantExists = this.productData.variants.some(
          (v) => v.options[0] === selectedColor && v.options[1] === size
        );

        if (!variantExists) {
          option.style.display = "none";
          option.setAttribute("tabindex", "-1");
          return;
        }

        option.style.display = "";
        option.setAttribute("tabindex", "0");

        const variant = this.productData.variants.find(
          (v) => v.options[0] === selectedColor && v.options[1] === size
        );

        const isAvailable = variant?.available;

        option.classList.toggle("sold-out", !isAvailable);
        option.classList.toggle("not-available", !isAvailable);

        if (!isAvailable) {
          option.setAttribute("aria-disabled", "true");
          option.setAttribute("onclick", `openNotifyMeModal('${variant.id}', '${size}')`);
        } else {
          option.removeAttribute("aria-disabled");
          option.removeAttribute("onclick");
        }
      }
    });
  }

  clearSizeSelection() {
    const productInfo = this.closest(".product-information");
    if (!productInfo) return;

    delete this.selectedOptions.Size;

    const sizeOptions = productInfo.querySelectorAll(".variant-size-option");
    sizeOptions.forEach((option) => {
      option.classList.remove("active");
    });
  }

  updateGalleryForColor(colorValue) {
    const productInfo = this.closest(".product-information");
    if (!productInfo) return;

    const colorVariants = this.productData.variants.filter(
      (v) => v.options[0] === colorValue
    );
    if (!colorVariants.length) return;

    const featuredMediaId = colorVariants[0]?.featured_media?.id;
    if (!featuredMediaId) return;

    const slides = productInfo.querySelectorAll(".embla__slide");
    const previews = productInfo.querySelectorAll("[data-preview-index]");
    if (!slides.length) return;

    const { startIndex, endIndex } = this.findColorImageRange(
      colorValue,
      featuredMediaId,
      slides
    );

    slides.forEach((slide, index) => {
      const shouldShow = index >= startIndex && index < endIndex;

      if (shouldShow) {
        slide.style.display = "";
        slide.classList.remove("is-hidden");
      } else {
        slide.style.display = "none";
        slide.classList.add("is-hidden");
      }
    });

    previews.forEach((preview, index) => {
      const shouldShow = index >= startIndex && index < endIndex;
      preview.style.display = shouldShow ? "" : "none";
      preview.classList.toggle("active", index === startIndex);
    });

    this.reinitializeCarousel();
  }

  findColorImageRange(colorValue, featuredMediaId, slides) {
    let startIndex = 0;
    let endIndex = slides.length;

    slides.forEach((slide, index) => {
      const mediaId = slide.querySelector("[data-media-id]")?.dataset.mediaId;
      if (mediaId && parseInt(mediaId) === parseInt(featuredMediaId)) {
        startIndex = index;
      }
    });

    const otherColorMediaIds = this.getOtherColorsFeaturedMediaIds(colorValue);

    for (let i = startIndex + 1; i < slides.length; i++) {
      const mediaId =
        slides[i].querySelector("[data-media-id]")?.dataset.mediaId;
      if (mediaId && otherColorMediaIds.includes(parseInt(mediaId))) {
        endIndex = i;
        break;
      }
    }

    return { startIndex, endIndex };
  }

  getOtherColorsFeaturedMediaIds(selectedColor) {
    const allColors = [
      ...new Set(this.productData.variants.map((v) => v.options[0])),
    ];

    return allColors
      .filter((color) => color !== selectedColor)
      .map((color) => {
        const variant = this.productData.variants.find(
          (v) => v.options[0] === color
        );
        return variant?.featured_media?.id;
      })
      .filter(Boolean);
  }

  reinitializeCarousel() {
    const productInfo = this.closest(".product-information");
    if (!productInfo) return;

    const emblaNode = productInfo.querySelector(".embla");
    if (!emblaNode || typeof EmblaCarousel === "undefined") return;

    if (window.emblaApi) {
      try {
        window.emblaApi.destroy();
      } catch (e) {
        console.warn("Error destroying Embla:", e);
      }
    }

    window.emblaApi = EmblaCarousel(emblaNode, {
      loop: false,
      dragFree: false,
      containScroll: "trimSnaps",
      slidesToScroll: 1,
      skipSnaps: false,
      dragThreshold: 15,
      inViewThreshold: 0.6,
      slides: ".embla__slide:not(.is-hidden)",
    });

    const updateUI = () => {
      const selectedIndex = window.emblaApi.selectedScrollSnap();

      const dots = productInfo.querySelectorAll(".embla__dot");
      dots.forEach((dot, index) => {
        dot.classList.toggle("embla__dot--selected", index === selectedIndex);
      });

      const visiblePreviews = productInfo.querySelectorAll(
        '[data-preview-index]:not([style*="display: none"])'
      );
      visiblePreviews.forEach((preview, index) => {
        preview.classList.toggle("active", index === selectedIndex);
      });
    };

    window.emblaApi.on("select", () => {
      updateUI();
      if (typeof window.muteAllGalleryVideos === 'function') {
        window.muteAllGalleryVideos();
      }
    });
    window.emblaApi.on("settle", updateUI);
    window.emblaApi.on("init", updateUI);

    this.bindPreviewClicks(productInfo);

    if (typeof window.bindGalleryArrows === 'function') {
      window.bindGalleryArrows(window.emblaApi);
    }

    requestAnimationFrame(() => {
      window.emblaApi.scrollTo(0);
      updateUI();
    });

    this.updateDots(productInfo);
  }

  bindPreviewClicks(productInfo) {
    const visiblePreviews = Array.from(
      productInfo.querySelectorAll("[data-preview-index]")
    ).filter((p) => p.style.display !== "none");

    visiblePreviews.forEach((preview, visibleIndex) => {
      const newPreview = preview.cloneNode(true);
      preview.replaceWith(newPreview);

      newPreview.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (typeof window.muteAllGalleryVideos === 'function') {
          window.muteAllGalleryVideos();
        }
        window.emblaApi?.scrollTo(visibleIndex);
      });

      newPreview.style.cursor = "pointer";
      newPreview.style.pointerEvents = "auto";
    });
  }

  updateDots(productInfo) {
    const dotsContainer = productInfo.querySelector(".embla__dots");
    const visibleCount = productInfo.querySelectorAll(
      ".embla__slide:not(.is-hidden)"
    ).length;

    if (typeof window.renderEmblaDots === "function") {
      window.renderEmblaDots(dotsContainer, visibleCount, window.emblaApi);
    }
  }

  updatePageContent(variant) {
    if (!variant) return;

    // Update price and discount badge using client-side data (instant, no network request)
    this.updatePriceClientSide(variant);
    this.updateDiscountBadgeClientSide(variant);
    this.updateButtons();
  }

  updatePrice(doc) {
    const productInfo = this.closest(".product-information");
    const productId = this.productData.id;

    const priceClass = `product-price-${productId}`;

    const currentPrice = productInfo?.querySelector(`.${priceClass}`);
    const newPrice = doc.querySelector(`.${priceClass}`);

    if (currentPrice && newPrice) {
      currentPrice.innerHTML = newPrice.innerHTML;
    }
  }

  updateDiscountBadge(doc) {
    const productInfo = this.closest(".product-information");
    const productId = this.productData.id;

    const currentBadge = productInfo.querySelector(
      `.product-discount-${productId}`
    );
    const newBadge = doc.querySelector(`.product-discount-${productId}`);

    if (currentBadge && newBadge) {
      currentBadge.innerHTML = newBadge.innerHTML;
      currentBadge.style.display = newBadge.style.display;
    }
  }

  updatePriceClientSide(variant) {
    const productInfo = this.closest(".product-information");
    const productId = this.productData.id;
    const priceElement = productInfo?.querySelector(
      `.product-price-${productId}`
    );

    if (!priceElement) return;

    // Update spans in place — replacing the row's innerHTML would wipe other children (e.g. Judge.me rating badge)
    let currentPrice = priceElement.querySelector(".current-price");
    if (!currentPrice) {
      currentPrice = document.createElement("span");
      currentPrice.className = "current-price heading-product";
      priceElement.prepend(currentPrice);
    }
    currentPrice.textContent = variant.price_formatted;

    let comparePrice = priceElement.querySelector(
      ".compare-at-price, .compared-at-price"
    );
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      if (!comparePrice) {
        comparePrice = document.createElement("span");
        comparePrice.className = "compare-at-price";
        currentPrice.after(comparePrice);
      }
      comparePrice.textContent = variant.compare_at_price_formatted;
    } else if (comparePrice) {
      comparePrice.remove();
    }
  }

  updateDiscountBadgeClientSide(variant) {
    const productInfo = this.closest(".product-information");
    const productId = this.productData.id;
    const badgeElement = productInfo?.querySelector(
      `.product-discount-${productId}`
    );

    if (!badgeElement) return;

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const discount = Math.abs(Math.round(
        ((variant.compare_at_price - variant.price) /
          variant.compare_at_price) *
          100
      ));
      badgeElement.innerHTML = `<span>${discount}% OFF</span>`;
      badgeElement.style.display = "";
    } else {
      badgeElement.style.display = "none";
    }
  }

  updateButtons() {
    const productInfo = this.closest(".product-information");

    const variant = this.findVariant();

    const addToCartButton = productInfo.querySelector("add-to-cart-button");

    if (addToCartButton) {
      if (variant) {
        addToCartButton.dataset.variantId = String(variant.id);
        addToCartButton.dataset.available = String(variant.available);
        addToCartButton.dataset.variantSelected = "true";
      } else {
        addToCartButton.dataset.variantSelected = "false";
        delete addToCartButton.dataset.variantId;
      }

      if (addToCartButton.updateButtonState) {
        addToCartButton.updateButtonState();
      }
    }

    const buyNowButton = productInfo.querySelector("buy-now-button");

    if (buyNowButton) {
      if (variant) {
        buyNowButton.setAttribute("data-variant-id", variant.id);
        buyNowButton.setAttribute(
          "data-available",
          variant.available.toString()
        );
        buyNowButton.setAttribute("data-variant-selected", "true");
      } else {
        buyNowButton.setAttribute("data-variant-selected", "false");
      }
    }
  }

  updateURL(url) {
    window.history.replaceState({}, "", url);
  }

  hideSizeNotSelectedMessage() {
    const messageElement = document.getElementById("size-not-selected-message");
    if (messageElement) {
      messageElement.classList.remove("show");
    }
  }

  showSizeNotSelectedMessage() {
    const messageElement = document.getElementById("size-not-selected-message");
    if (messageElement) {
      messageElement.classList.add("show");
    }
  }

  dispatchVariantChange(variant, isColorSoldOut = false) {
    this.dispatchEvent(
      new CustomEvent("variant:changed", {
        bubbles: true,
        detail: {
          selectedVariant: variant,
          variant,
          selectedOptions: this.selectedOptions,
          options: this.selectedOptions,
          variantId: variant?.id || null,
          productId: this.productData.id,
          isColorSoldOut: isColorSoldOut,
        },
      })
    );
  }

  updateModelSize(variant) {
    if (!variant) return;

    const modelSizeElement = document.getElementById("model-size-description");
    const modelSizesData = document.getElementById("variant-model-sizes");

    if (!modelSizeElement || !modelSizesData) return;

    let modelSizes = {};
    try {
      modelSizes = JSON.parse(modelSizesData.textContent);
    } catch (error) {
      console.error("Error parsing variant model sizes:", error);
      return;
    }

    const variantId = String(variant.id);
    const modelSizeHtml = modelSizes[variantId];

    if (modelSizeHtml) {
      modelSizeElement.innerHTML = modelSizeHtml;
      modelSizeElement.style.display = "";
    }
  }
}

if (!customElements.get("variant-selects")) {
  customElements.define("variant-selects", VariantSelects);
}

window.VariantSelects = VariantSelects;

const muteAllGalleryVideos = () => {
  const videos = document.querySelectorAll('.product-video-wrapper video');
  
  for (const video of videos) {
    video.muted = true;
    
    const wrapper = video.closest('.product-video-wrapper');
    if (!wrapper) continue;
    
    const muteButton = wrapper.querySelector('.video-mute-button');
    if (!muteButton) continue;
    
    muteButton.dataset.muted = 'true';
    const volumeOnIcon = muteButton.querySelector('.volume-on-icon');
    const volumeOffIcon = muteButton.querySelector('.volume-off-icon');
    
    if (volumeOnIcon) volumeOnIcon.style.display = 'none';
    if (volumeOffIcon) volumeOffIcon.style.display = '';
    muteButton.setAttribute('aria-label', 'Unmute video');
  }
};

window.muteAllGalleryVideos = muteAllGalleryVideos;

const renderEmblaDots = (dotsContainer, slideCount, emblaApi) => {
  if (!dotsContainer) return;

  dotsContainer.innerHTML = "";
  for (let i = 0; i < slideCount; i++) {
    const dot = document.createElement("button");
    dot.className = `embla__dot${i === 0 ? " embla__dot--selected" : ""}`;
    dot.setAttribute("type", "button");
    dot.setAttribute("aria-label", `Go to slide ${i + 1}`);

    dot.addEventListener("click", () => emblaApi?.scrollTo(i));

    dotsContainer.appendChild(dot);
  }
};

window.renderEmblaDots = renderEmblaDots;

const initProductGallery = () => {
  const emblaNode = document.querySelector(".product-information .embla");
  const previewsContainer = document.querySelector('[data-gallery="previews"]');
  const productInfoContainer = document.querySelector(".product-information");

  if (!emblaNode || !previewsContainer) return;

  const hasColorSwatches = document.querySelector(".color-swatch");
  if (hasColorSwatches) {
    setupGalleryHelpers(previewsContainer);
    setupGalleryArrows(emblaNode);
    return;
  }

  const emblaApi = EmblaCarousel(emblaNode, {
    loop: false,
    dragFree: false,
    containScroll: "trimSnaps",
    slidesToScroll: 1,
    skipSnaps: false,
    dragThreshold: 15,
    inViewThreshold: 0.6,
  });

  window.emblaApi = emblaApi;

  const dotsContainer = emblaNode.querySelector(".embla__dots");
  const slideCount = emblaNode.querySelectorAll(".embla__slide:not(.is-hidden)").length;
  renderEmblaDots(dotsContainer, slideCount, emblaApi);

  setupGalleryHelpers(previewsContainer, emblaApi);
  setupGalleryArrows(emblaNode, emblaApi);

  emblaApi.on("select", () => {
    const selectedIndex = emblaApi.selectedScrollSnap();
    if (typeof window.updateGalleryDotsAndPreviews === "function") {
      window.updateGalleryDotsAndPreviews(selectedIndex);
    }
    muteAllGalleryVideos();
  });

  emblaApi.on("settle", () => {
    const selectedIndex = emblaApi.selectedScrollSnap();
    if (typeof window.updateGalleryDotsAndPreviews === "function") {
      window.updateGalleryDotsAndPreviews(selectedIndex);
    }
  });

  emblaApi.on("init", () => {
    const selectedIndex = emblaApi.selectedScrollSnap();
    if (typeof window.updateGalleryDotsAndPreviews === "function") {
      window.updateGalleryDotsAndPreviews(selectedIndex);
    }
  });

  if (typeof window.bindGalleryPreviews === "function") {
    window.bindGalleryPreviews();
  }
};

function setupGalleryArrows(emblaNode, emblaApi = null) {
  const prevBtn = emblaNode.querySelector('.embla__arrow--prev');
  const nextBtn = emblaNode.querySelector('.embla__arrow--next');

  if (!prevBtn || !nextBtn) return;

  const updateArrowState = (api) => {
    prevBtn.disabled = !api.canScrollPrev();
    nextBtn.disabled = !api.canScrollNext();
  };

  const bindArrows = (api) => {
    prevBtn.onclick = () => api.scrollPrev();
    nextBtn.onclick = () => api.scrollNext();
    api.on('select', () => updateArrowState(api));
    api.on('init', () => updateArrowState(api));
    updateArrowState(api);
  };

  if (emblaApi) {
    bindArrows(emblaApi);
  }

  // Re-bind when variant-selects reinitializes the carousel
  window.bindGalleryArrows = (api) => bindArrows(api);
}

function setupGalleryHelpers(previewsContainer, emblaApi = null) {
  const previewItems = previewsContainer.querySelectorAll(
    "[data-preview-index]"
  );

  const updateActivePreview = (activeIndex) => {
    const visiblePreviews = Array.from(previewItems).filter(
      (item) => item.style.display !== "none"
    );

    visiblePreviews.forEach((item, index) => {
      item.classList.toggle("active", index === activeIndex);
    });
  };

  const updateDotsAndPreviews = (selectedIndex) => {
    updateActivePreview(selectedIndex);

    const dots = document.querySelectorAll(".embla__dot");
    dots.forEach((dot, index) => {
      dot.classList.toggle("embla__dot--selected", index === selectedIndex);
    });
  };

  window.updateGalleryDotsAndPreviews = updateDotsAndPreviews;

  window.bindGalleryPreviews = () => {
    const currentPreviewItems = previewsContainer.querySelectorAll(
      "[data-preview-index]:not([style*='display: none'])"
    );

    currentPreviewItems.forEach((item, visibleIndex) => {
      const handleClick = () => {
        if (window.muteAllGalleryVideos) {
          window.muteAllGalleryVideos();
        }
        if (window.emblaApi) {
          window.emblaApi.scrollTo(visibleIndex);
        }
      };

      const newItem = item.cloneNode(true);
      item.parentNode.replaceChild(newItem, item);

      newItem.addEventListener("click", handleClick);
      newItem.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      });
    });
  };
}

const initStickyButtonsFooterHiding = () => {
  if (window.innerWidth >= 768) return;

  const stickyButtons = document.querySelector(
    ".product-cart-buttons-container"
  );
  const footer = document.querySelector(
    'footer, .footer, [data-section-type="footer"], .shopify-section-footer, .site-footer, #shopify-section-footer, [id*="footer"]'
  );

  if (!stickyButtons || !footer) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          stickyButtons.classList.add("hidden-by-footer");
        } else {
          stickyButtons.classList.remove("hidden-by-footer");
        }
      });
    },
    {
      root: null,
      rootMargin: "0px 0px -50px 0px",
      threshold: [0, 0.1, 0.25],
    }
  );

  observer.observe(footer);

  const handleResize = () => {
    if (window.innerWidth >= 768) {
      observer.disconnect();
      stickyButtons.classList.remove("hidden-by-footer");
    }
  };

  window.addEventListener("resize", handleResize);
};

const initModelSizeUpdater = () => {
  const modelSizeElement = document.getElementById("model-size-description");
  const modelSizesData = document.getElementById("variant-model-sizes");

  if (!modelSizeElement || !modelSizesData) return;

  let modelSizes = {};
  try {
    modelSizes = JSON.parse(modelSizesData.textContent);
  } catch (error) {
    console.error("Error parsing variant model sizes:", error);
    return;
  }

  const hasVariantLevelSizes = Object.values(modelSizes).some(
    (size) => size !== null
  );

  // If all variants have null (using product fallback), don't update on variant change
  if (!hasVariantLevelSizes) return;

  const updateModelSize = (event) => {
    if (event.detail && event.detail.selectedVariant) {
      const variantId = String(event.detail.selectedVariant.id);
      const modelSizeHtml = modelSizes[variantId];

      if (modelSizeHtml) {
        modelSizeElement.innerHTML = modelSizeHtml;
        modelSizeElement.style.display = "";
      }
    }
  };

  document.addEventListener("variant:changed", updateModelSize);
};

const initVideoMuteControls = () => {
  const muteButtons = document.querySelectorAll('.video-mute-button');
  
  for (const button of muteButtons) {
    const videoWrapper = button.closest('.product-video-wrapper');
    if (!videoWrapper) continue;
    
    const video = videoWrapper.querySelector('video');
    if (!video) continue;
    
    // Start muted by default for autoplay compatibility
    video.muted = true;
    button.dataset.muted = 'true';
    button.querySelector('.volume-on-icon').style.display = 'none';
    button.querySelector('.volume-off-icon').style.display = '';
    button.setAttribute('aria-label', 'Unmute video');
    
    button.addEventListener('click', () => {
      const isMuted = video.muted;
      video.muted = !isMuted;
      button.dataset.muted = String(!isMuted);
      
      const volumeOnIcon = button.querySelector('.volume-on-icon');
      const volumeOffIcon = button.querySelector('.volume-off-icon');
      
      if (video.muted) {
        volumeOnIcon.style.display = 'none';
        volumeOffIcon.style.display = '';
        button.setAttribute('aria-label', 'Unmute video');
      } else {
        volumeOnIcon.style.display = '';
        volumeOffIcon.style.display = 'none';
        button.setAttribute('aria-label', 'Mute video');
      }
    });
  }
};

const initGalleryHoverTooltip = () => {
  const galleryPreviews = document.querySelector('.product-information .gallery-previews');
  if (!galleryPreviews) return;

  const tooltip = document.querySelector('.gallery-hover-tooltip');
  if (!tooltip) return;

  galleryPreviews.addEventListener('mouseover', (e) => {
    const item = e.target.closest('.gallery-preview-item');
    if (!item) return;

    const isVideo = item.classList.contains('video-thumbnail');
    const tooltipImage = tooltip.querySelector('.gallery-hover-tooltip__image');
    const tooltipVideo = tooltip.querySelector('.gallery-hover-tooltip__video');

    if (isVideo) {
      const previewIndex = item.dataset.previewIndex;
      const slide = document.querySelector(`.embla__slide[data-slide-index="${previewIndex}"]`);
      if (!slide) return;

      const video = slide.querySelector('video');
      if (!video) return;

      const source = video.querySelector('source');
      const videoSrc = source ? source.src : video.src;

      tooltipVideo.src = videoSrc;
      tooltipVideo.style.display = '';
      tooltipImage.style.display = 'none';
      tooltipVideo.play();
    } else {
      const previewIndex = item.dataset.previewIndex;
      const slide = document.querySelector(`.embla__slide[data-slide-index="${previewIndex}"]`);
      const slideImg = slide?.querySelector('.product-gallery-image');

      if (slideImg) {
        tooltipImage.src = slideImg.src;
      } else {
        const img = item.tagName === 'IMG' ? item : item.querySelector('img');
        if (!img) return;
        tooltipImage.src = img.src.replace('width=50', 'width=400');
      }

      tooltipImage.style.display = '';
      tooltipVideo.style.display = 'none';
    }

    const galleryContainer = document.querySelector('.product-gallery-container');
    if (!galleryContainer) return;

    const rawTop = item.offsetTop + (item.offsetHeight / 2) - 100;
    const clampedTop = Math.max(0, Math.min(rawTop, galleryContainer.offsetHeight - 200));
    tooltip.style.top = clampedTop + 'px';
    tooltip.style.display = '';
    tooltip.classList.add('visible');
  }, { capture: false });

  galleryPreviews.addEventListener('mouseout', (e) => {
    const item = e.target.closest('.gallery-preview-item');
    if (!item) return;

    const related = e.relatedTarget;
    if (related && item.contains(related)) return;

    tooltip.classList.remove('visible');
    tooltip.style.display = 'none';

    const tooltipVideo = tooltip.querySelector('.gallery-hover-tooltip__video');
    if (!tooltipVideo.paused) tooltipVideo.pause();
  }, { capture: false });
};

const initGalleryLightbox = () => {
  const lightbox = document.querySelector('.gallery-lightbox');
  if (!lightbox) return;

  const lightboxImage = lightbox.querySelector('.gallery-lightbox__image');
  const lightboxVideoWrapper = lightbox.querySelector('.gallery-lightbox__video-wrapper');
  const lightboxVideo = lightbox.querySelector('.gallery-lightbox__video');
  const lightboxPlayPause = lightbox.querySelector('.gallery-lightbox__play-pause');
  const lightboxOverlay = lightbox.querySelector('.gallery-lightbox__overlay');
  const lightboxClose = lightbox.querySelector('.gallery-lightbox__close');
  let scrollY = 0;

  const updatePlayPauseIcon = () => {
    const isPlaying = !lightboxVideo.paused;
    lightboxPlayPause.style.display = isPlaying ? 'none' : '';
    lightboxVideoWrapper.classList.toggle('is-playing', isPlaying);
  };

  const togglePlayPause = () => {
    if (lightboxVideo.paused) {
      lightboxVideo.play();
    } else {
      lightboxVideo.pause();
    }
  };

  lightboxPlayPause.addEventListener('click', (e) => {
    e.stopPropagation();
    togglePlayPause();
  });

  lightboxVideo.addEventListener('click', togglePlayPause);
  lightboxVideo.addEventListener('play', updatePlayPauseIcon);
  lightboxVideo.addEventListener('pause', updatePlayPauseIcon);
  lightboxVideo.addEventListener('ended', updatePlayPauseIcon);

  const openLightbox = (imgSrc, videoSrc) => {
    if (imgSrc) {
      lightboxImage.src = imgSrc;
      lightboxImage.style.display = '';
      lightboxVideoWrapper.style.display = 'none';
      if (!lightboxVideo.paused) lightboxVideo.pause();
    }

    if (videoSrc) {
      lightboxVideo.src = videoSrc;
      lightboxVideoWrapper.style.display = '';
      lightboxImage.style.display = 'none';
    }

    scrollY = window.scrollY;
    lightbox.style.display = 'flex';
    lightbox.setAttribute('aria-hidden', 'false');

    requestAnimationFrame(() => {
      lightbox.classList.add('open');
      if (videoSrc) lightboxVideo.play();
    });

    document.body.style.position = 'fixed';
    document.body.style.top = '-' + scrollY + 'px';
    document.body.style.width = '100%';
  };

  const closeLightbox = () => {
    lightbox.classList.remove('open');
    lightbox.style.display = 'none';
    lightbox.setAttribute('aria-hidden', 'true');

    if (!lightboxVideo.paused) lightboxVideo.pause();
    lightboxVideo.src = '';
    lightboxImage.src = '';

    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  };

  const galleryContainer = document.querySelector('.product-information .product-gallery-container');
  if (!galleryContainer) return;

  galleryContainer.addEventListener('click', (e) => {
    if (e.target.closest('.embla__arrow')) return;

    const clickedImage = e.target.closest('.product-gallery-image');
    if (clickedImage) {
      openLightbox(clickedImage.src, null);
      return;
    }

    const clickedVideo = e.target.closest('.product-gallery-video');
    if (clickedVideo) {
      const source = clickedVideo.querySelector('source');
      const videoSrc = clickedVideo.currentSrc || source?.src || clickedVideo.src;
      openLightbox(null, videoSrc);
    }
  });

  lightboxOverlay.addEventListener('click', closeLightbox);
  lightboxClose.addEventListener('click', closeLightbox);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && lightbox.classList.contains('open')) closeLightbox();
  });
};

const initReviewBadgeScroll = () => {
  const badge = document.querySelector(
    ".product-information .jdgm-widget.jdgm-preview-badge"
  );
  if (!badge) return;

  badge.style.cursor = "pointer";

  badge.addEventListener("click", () => {
    // Standard Judge.me review widget block is disabled on this theme — reviews live in the custom "Product Reviews" section instead
    const reviews = document.querySelector(".product-reviews");
    if (!reviews) return;

    const headerOffset = window.innerWidth >= 1024 ? 92 : 84;
    const top =
      reviews.getBoundingClientRect().top + window.scrollY - headerOffset - 20;
    window.scrollTo({ top, behavior: "smooth" });
  });
};

document.addEventListener("DOMContentLoaded", () => {
  initProductGallery();
  initStickyButtonsFooterHiding();
  initModelSizeUpdater();
  initVideoMuteControls();
  initGalleryHoverTooltip();
  initGalleryLightbox();
  initReviewBadgeScroll();
});

class FadeModalButton extends HTMLElement {
  connectedCallback() {
    this.clickable =
      this.querySelector("button") || this.querySelector("a") || this;
    this.targetModal = this.getAttribute("data-modal-target");
    this.handleClick = this.handleClick.bind(this);
    this.clickable.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.clickable?.removeEventListener("click", this.handleClick);
  }

  handleClick(event) {
    event.preventDefault();
    if (this.targetModal) {
      const modal = document.querySelector(
        `fade-modal[data-modal-id="${this.targetModal}"]`
      );
      if (modal) {
        modal.open();
      }
    }
  }
}

class FadeModal extends HTMLElement {
  connectedCallback() {
    this.overlay = this.querySelector(".modal-overlay");
    this.container = this.querySelector(".modal-container");
    this.closeBtn = this.querySelector(".modal-close");
    this.content = this.querySelector(".modal-content");
    this.isOpen = false;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);

    this.closeBtn?.addEventListener("click", this.close);
    this.overlay?.addEventListener("click", this.handleOverlayClick);

    this.setupStyles();
  }

  disconnectedCallback() {
    this.closeBtn?.removeEventListener("click", this.close);
    this.overlay?.removeEventListener("click", this.handleOverlayClick);
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  setupStyles() {
    if (!this.container) return;

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) {
      this.container.style.transform = "translateX(-50%) translateY(100%)";
    } else {
      this.container.style.transform = "translate(-50%, -50%) scale(0.95)";
    }
    this.container.style.opacity = "0";
    this.container.style.transition = "opacity 0.3s ease, transform 0.3s ease";
  }

  open() {
    if (this.isOpen) return;


    this.scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${this.scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.classList.add("fade-modal-open");

    this.style.display = "flex";
    this.classList.add("open");

    this.offsetHeight;

    setTimeout(() => {
      if (this.container) {
        this.container.style.opacity = "1";
        const isMobile = window.matchMedia("(max-width: 767px)").matches;
        if (isMobile) {
          this.container.style.transform = "translateX(-50%)";
        } else {
          this.container.style.transform = "translate(-50%, -50%) scale(1)";
        }
      }
      if (this.overlay) {
        this.overlay.style.opacity = "1";
      }
    }, 10);

    this.isOpen = true;
    document.addEventListener("keydown", this.handleKeyDown);

    const modalId = this.getAttribute("data-modal-id");
    window.dispatchEvent(
      new CustomEvent("fade-modal:open", {
        detail: { modalId },
      })
    );
  }

  close() {
    if (!this.isOpen) return;


    if (this.container) {
      this.container.style.opacity = "0";
      const isMobile = window.matchMedia("(max-width: 767px)").matches;
      if (isMobile) {
        this.container.style.transform = "translateX(-50%) translateY(100%)";
      } else {
        this.container.style.transform = "translate(-50%, -50%) scale(0.95)";
      }
    }
    if (this.overlay) {
      this.overlay.style.opacity = "0";
    }

    this.classList.remove("open");

    setTimeout(() => {
      this.style.display = "none";

      const scrollY = parseInt(document.body.style.top || "0") * -1;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.classList.remove("fade-modal-open");
      window.scrollTo(0, scrollY);
    }, 300);

    this.isOpen = false;
    document.removeEventListener("keydown", this.handleKeyDown);

    const modalId = this.getAttribute("data-modal-id");
    window.dispatchEvent(
      new CustomEvent("fade-modal:close", {
        detail: { modalId },
      })
    );
  }

  handleOverlayClick(event) {
    if (event.target === this.overlay) {
      this.close();
    }
  }

  handleKeyDown(event) {
    if (event.key === "Escape") {
      this.close();
    }
  }
}

if (!customElements.get("fade-modal-button")) {
  customElements.define("fade-modal-button", FadeModalButton);
}
if (!customElements.get("fade-modal")) {
  customElements.define("fade-modal", FadeModal);
}

class BuyNowButton extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.updateButtonState();

    const productContainer =
      this.closest(".product-information") ||
      this.closest("[data-product-data]");

    const variantChangeHandler = (event) => {
      if (event.detail) {
        if (event.detail.selectedVariant) {
          const variantId = event.detail.selectedVariant.id;
          const isAvailable = event.detail.selectedVariant.available;

          this.dataset.variantId = variantId;
          this.dataset.available = isAvailable.toString();
          this.dataset.variantSelected = "true";
          this.dataset.colorSoldOut = "false";
        } else {
          this.dataset.variantSelected = "false";
          this.dataset.colorSoldOut = event.detail.isColorSoldOut
            ? "true"
            : "false";
        }
        this.updateButtonState();
      }
    };

    document.addEventListener("variant:changed", variantChangeHandler);

    if (productContainer) {
      productContainer.addEventListener(
        "variant:changed",
        variantChangeHandler
      );
    }

    this.addEventListener("click", async (e) => {
      e.preventDefault();

      const variantSelected = this.dataset.variantSelected === "true";

      if (!variantSelected) {
        this.showSizeNotSelectedMessage();
        this.scrollToVariantSelection();
        return;
      }

      if (this.dataset.available === "false") {
        return;
      }

      const variantId = this.dataset.variantId;

      if (!variantId) {
        console.warn("Missing data-variant-id on <buy-now-button>");
        return;
      }

      // GoKwik Buy Now integration
      if (typeof window.onBuyNowClick === "function") {
        const qty = parseInt(this.dataset.quantity, 10) || 1;

        window.onBuyNowClick(e, false, [
          {
            id: parseInt(variantId, 10),
            quantity: qty,
          },
        ]);
      }
    });
  }

  updateButtonState() {
    const isAvailable = this.dataset.available !== "false";
    const variantId = this.dataset.variantId;
    const variantSelected = this.dataset.variantSelected === "true";
    const colorSoldOut = this.dataset.colorSoldOut === "true";

    this.classList.toggle("unavailable", !isAvailable && variantSelected);

    if (colorSoldOut) {
      this.style.display = "none";
    } else if (!variantSelected) {
      this.removeAttribute("disabled");
      this.removeAttribute("aria-disabled");
      this.style.pointerEvents = "";
      this.style.display = "";
      this.textContent = "BUY NOW";
    } else if (!isAvailable) {
      this.setAttribute("disabled", "");
      this.setAttribute("aria-disabled", "true");
      this.style.pointerEvents = "none";
      this.style.display = "none";
      this.textContent = variantId ? "SOLD OUT" : "UNAVAILABLE";
    } else {
      this.removeAttribute("disabled");
      this.removeAttribute("aria-disabled");
      this.style.pointerEvents = "";
      this.style.display = "";
      this.textContent = "BUY NOW";
    }
  }

  showSizeNotSelectedMessage() {
    const variantSelects = document.querySelector("variant-selects");

    if (variantSelects && variantSelects.showSizeNotSelectedMessage) {
      variantSelects.showSizeNotSelectedMessage();
    } else {
      const messageElement = document.getElementById(
        "size-not-selected-message"
      );
      if (messageElement) {
        messageElement.classList.add("show");
      }
    }
  }

  scrollToVariantSelection() {
    const productContainer =
      this.closest(".product-information") ||
      this.closest("[data-product-data]");

    if (!productContainer) return;

    const variantSelector = productContainer.querySelector(
      ".product-sizing-guide"
    );

    if (variantSelector) {
      variantSelector.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }

  showToast(message, type = "info") {
    try {
      const container = document.querySelector(".global-toast-container");
      if (!container) return;
      const toast = document.createElement("div");
      toast.className = `global-toast global-toast--${type}`;
      toast.textContent = message;
      container.appendChild(toast);
      setTimeout(() => toast.classList.add("global-toast--visible"), 50);
      setTimeout(() => {
        toast.classList.remove("global-toast--visible");
        setTimeout(() => toast.remove(), 300);
      }, 4000);
    } catch (err) {
      console.warn("Toast error", err);
    }
  }
}

if (!customElements.get("buy-now-button")) {
  customElements.define("buy-now-button", BuyNowButton);
}
